import { HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  GoogleAnalyticsService,
  IndexedDBService,
  LocaleSetting,
  electronSystemLocaleKey,
  indexedDbConfig,
  navigatorLocaleKey
} from '@rct/lynx-lobbies-shared-lib';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NewTabModule } from '@rct/lynx-lobbies-shared-lib';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { Observable } from 'rxjs';
import { concatMap, take } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './core/components/shared.module';
import { LynxApiInterceptor } from './core/interceptors/lynx-api.interceptor';
import { LynxErrorInterceptor } from './core/interceptors/lynx-error.interceptor';
import { LynxElectronService } from './core/services/electron.service';
import { LynxLocaleSettingService } from './core/services/locale-setting.service';
import { HomeModule } from './pages/home/home.module';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';

export function HttpLoaderFactory(_httpBackend: HttpBackend) {
  return new MultiTranslateHttpLoader(_httpBackend, [
    { prefix: './assets/i18n/', optional: true }
  ]);
}

export function startupServiceFactory(
  googleAnalyticsService: GoogleAnalyticsService
): Function {
  return () => googleAnalyticsService.loadGoogleAnalytics();
}

export function initializeBrowserLocaleFactory(
  electronService: LynxElectronService,
  indexedDbService: IndexedDBService,
  localeSettingService: LynxLocaleSettingService
): () => Observable<LocaleSetting> {
  return () => {
    electronService.loadElectronWrapperSystemLocale();
    return indexedDbService.seedLynxDbIfEmpty$().pipe(
      concatMap((_) =>
        electronService.getElectronWrapperSystemLocale$().pipe(
          concatMap((electronSystemLocale) =>
            localeSettingService.updateLocaleSetting$({
              [electronSystemLocaleKey]: electronSystemLocale,
              [navigatorLocaleKey]: navigator.language
            })
          ),
          concatMap(() => localeSettingService.getLocaleSetting$())
        )
      ),
      take(1)
    );
  };
}

@NgModule({ declarations: [AppComponent, PageNotFoundComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpBackend]
            }
        }),
        HomeModule,
        SharedModule,
        AppRoutingModule,
        RecaptchaV3Module,
        NewTabModule,
        NgxIndexedDBModule.forRoot(indexedDbConfig)], providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LynxApiInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LynxErrorInterceptor,
            multi: true
        },
        provideAppInitializer(() => {
        const initializerFn = (startupServiceFactory)(inject(GoogleAnalyticsService));
        return initializerFn();
      }),
        provideAppInitializer(() => {
        const initializerFn = (initializeBrowserLocaleFactory)(inject(LynxElectronService), inject(IndexedDBService), inject(LynxLocaleSettingService));
        return initializerFn();
      }),
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useValue: environment.googleReCaptcha.siteKey
        },
        { provide: 'environment', useValue: environment },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {}
